import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Sunrise, Copy, Search, CheckCircle, Calendar, Target, TrendingUp, RefreshCw, Loader2, Send } from "lucide-react";
import { useMyTasksAllBrands } from "@/hooks/useMyTasksAllBrands";
import { useGrowthStandup, TaskForStandup } from "@/hooks/useGrowthStandup";
import { useSlackApi } from "@/hooks/useSlackApi";
import { useAuth } from "@/hooks/useAuth";
import { useTaskCreationSettings } from "@/hooks/useTaskCreationSettings";
import { useBrandManager } from "@/hooks/useBrandManager";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface DayStartDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onComplete?: () => void;
  /** When true, render content inline (no modal). For holdco views. */
  embedded?: boolean;
}

const formatTime = (minutes: number | null | undefined) => {
  if (!minutes || minutes === 0) return '-';
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

const getTypeIcon = (type: 'foundational' | 'growth' | 'recurring') => {
  switch (type) {
    case 'foundational':
      return <Target className="h-3 w-3 text-blue-500 flex-shrink-0" />;
    case 'growth':
      return <TrendingUp className="h-3 w-3 text-green-500 flex-shrink-0" />;
    case 'recurring':
      return <RefreshCw className="h-3 w-3 text-purple-500 flex-shrink-0" />;
  }
};

const getTypeLabel = (type: 'foundational' | 'growth' | 'recurring') => {
  switch (type) {
    case 'foundational':
      return 'Foundational Task';
    case 'growth':
      return 'Growth Task';
    case 'recurring':
      return 'Recurring Task';
  }
};

export const DayStartDialog: React.FC<DayStartDialogProps> = ({
  open = true,
  onOpenChange,
  onComplete,
  embedded = false,
}) => {
  const { foundationalTasks, growthTasks, recurringTasks, isLoading: tasksLoading } = useMyTasksAllBrands();
  const { addToStandup, copyToClipboard, formatForSlack, isLoading, standupTasks, hasTodayStandup, isLoadingStandup } = useGrowthStandup();
  const { sendNotification, isLoading: slackLoading } = useSlackApi();
  const { user } = useAuth();
  const { settings } = useTaskCreationSettings();
  const { currentBrandId } = useBrandManager();
  
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isAlreadyPlannedHovered, setIsAlreadyPlannedHovered] = useState(false);
  const [isSendingPlanned, setIsSendingPlanned] = useState(false);

  // Combine all pending/in_progress tasks
  const allTasks: TaskForStandup[] = useMemo(() => {
    const tasks: TaskForStandup[] = [];

    // Foundational tasks
    foundationalTasks
      .filter(t => t.status === 'pending' || t.status === 'in_progress')
      .forEach(task => {
        tasks.push({
          id: task.id,
          title: task.title,
          type: 'foundational',
          status: task.status,
          due_date: task.due_date || null,
          time_estimated: (task as any).time_estimated,
          time_spent: (task as any).time_spent,
          brand_id: task.brand.id,
          brand_name: task.brand.name,
          brand_slug: task.brand.slug,
        });
      });

    // Growth tasks
    growthTasks
      .filter(t => (t.task_status || 'pending') === 'pending' || (t.task_status || 'pending') === 'in_progress')
      .forEach(task => {
        tasks.push({
          id: task.id,
          title: task.name,
          type: 'growth',
          status: task.task_status || 'pending',
          due_date: task.due_date || null,
          time_estimated: (task as any).time_estimated,
          time_spent: (task as any).time_spent,
          brand_id: task.brand.id,
          brand_name: task.brand.name,
          brand_slug: task.brand.slug,
        });
      });

    // Recurring tasks
    recurringTasks
      .filter(t => t.status === 'pending' || t.status === 'in_progress')
      .forEach(task => {
        tasks.push({
          id: task.id,
          title: task.title,
          type: 'recurring',
          status: task.status,
          due_date: (task as any).due_date || null,
          time_estimated: (task as any).time_estimated,
          time_spent: (task as any).time_spent,
          brand_id: task.brand.id,
          brand_name: task.brand.name,
          brand_slug: task.brand.slug,
        });
      });

    // Sort by due date (earliest first), null dates at end
    return tasks.sort((a, b) => {
      if (!a.due_date && !b.due_date) return 0;
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    });
  }, [foundationalTasks, growthTasks, recurringTasks]);

  // Filter by search query
  const filteredTasks = useMemo(() => {
    if (!searchQuery) return allTasks;
    const query = searchQuery.toLowerCase();
    return allTasks.filter(t => 
      t.title.toLowerCase().includes(query) || 
      t.brand_name.toLowerCase().includes(query)
    );
  }, [allTasks, searchQuery]);

  // Group by due date
  const tasksByDate = useMemo(() => {
    const grouped: Record<string, TaskForStandup[]> = {};
    filteredTasks.forEach(task => {
      const dateKey = task.due_date 
        ? format(new Date(task.due_date), 'yyyy-MM-dd') 
        : 'No due date';
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(task);
    });
    return grouped;
  }, [filteredTasks]);

  const selectedTasks = allTasks.filter(t => selectedTaskIds.has(t.id));

  const handleToggle = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedTaskIds.size === filteredTasks.length) {
      setSelectedTaskIds(new Set());
    } else {
      setSelectedTaskIds(new Set(filteredTasks.map(t => t.id)));
    }
  };

  const handleSelectDateGroup = (dateKey: string) => {
    const dateTasks = tasksByDate[dateKey] || [];
    const allSelected = dateTasks.every(t => selectedTaskIds.has(t.id));
    
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (allSelected) {
        dateTasks.forEach(t => next.delete(t.id));
      } else {
        dateTasks.forEach(t => next.add(t.id));
      }
      return next;
    });
  };

  const handleAddToStandup = async () => {
    if (selectedTasks.length === 0) return;
    
    const success = await addToStandup(selectedTasks);
    if (success) {
      // Upsert submission record to track Day Start
      const today = format(new Date(), 'yyyy-MM-dd');
      const totalTaskCount = standupTasks.length + selectedTasks.length;
      
      try {
        const { error: submissionError } = await supabase
          .from('os_growth_standup_submissions')
          .upsert({
            user_id: user?.id,
            brand_id: currentBrandId,
            submission_date: today,
            day_start_at: new Date().toISOString(),
            day_start_task_count: totalTaskCount,
          }, { onConflict: 'user_id,submission_date' });
        
        if (submissionError) {
          console.error('Error upserting submission:', submissionError);
        }
      } catch (err) {
        console.error('Error tracking submission:', err);
      }
      
      // Send to Slack after adding - use configured report brand, fallback to current brand
      const userName = user?.user_metadata?.full_name || user?.email || 'User';
      const message = formatForSlack(selectedTasks, userName);
      
      // Cast settings to include new field
      const typedSettings = settings as typeof settings & { growth_standup_report_brand_id?: string | null };
      const reportBrandId = typedSettings?.growth_standup_report_brand_id || currentBrandId;
      
      if (reportBrandId) {
        await sendNotification(message, undefined, reportBrandId);
      }
      
      setSelectedTaskIds(new Set());
      onComplete?.();
      if (!embedded) onOpenChange?.(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (selectedTasks.length === 0) return;
    const userName = user?.user_metadata?.full_name || user?.email || 'User';
    await copyToClipboard(selectedTasks, userName);
  };

  // Send already planned tasks to Slack
  const handleSendPlannedToSlack = async () => {
    if (standupTasks.length === 0) return;
    
    setIsSendingPlanned(true);
    try {
      const userName = user?.user_metadata?.full_name || user?.email || 'User';
      const today = format(new Date(), 'MMMM d, yyyy');
      
      let message = `*Day Start (Re-sync) - ${today} - ${userName}*\n\n`;
      
      standupTasks.forEach(task => {
        const spentTime = formatTime(task.time_spent);
        message += `${task.brand_name} - ${task.task_title} - Spent: ${spentTime}\n`;
      });
      
      const totalSpent = standupTasks.reduce((acc, t) => acc + (t.time_spent || 0), 0);
      message += `\n*Total: ${standupTasks.length} tasks | Spent: ${formatTime(totalSpent)}*`;
      
      const typedSettings = settings as typeof settings & { growth_standup_report_brand_id?: string | null };
      const reportBrandId = typedSettings?.growth_standup_report_brand_id || currentBrandId;
      
      if (reportBrandId) {
        await sendNotification(message, undefined, reportBrandId);
      }
    } finally {
      setIsSendingPlanned(false);
    }
  };

  const headerContent = (
    <>
      <div className="flex items-center gap-2">
        <Sunrise className="h-5 w-5 text-amber-500" />
        <span className="text-lg font-semibold">Day Start</span>
        <span className="text-sm font-normal text-muted-foreground">
          {format(new Date(), 'EEEE, MMMM d, yyyy')}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Good morning <span className="font-semibold text-foreground">{user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'there'}</span>, let&apos;s make the day count.
      </p>
    </>
  );

  const bodyContent = (
    <>
        <div className="flex items-center gap-2 px-6 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
          >
            {selectedTaskIds.size === filteredTasks.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>

        {selectedTaskIds.size > 0 && (
          <div className="flex items-center gap-2 px-6 py-1">
            <Badge variant="secondary" className="bg-primary/10 text-primary">
              <CheckCircle className="h-3 w-3 mr-1" />
              {selectedTaskIds.size} selected
            </Badge>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 pb-4">
          {/* Loading state */}
          {(tasksLoading || isLoadingStandup) && (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin mb-3 text-primary" />
              <p className="text-lg font-medium">Retrieving Tasks</p>
              <p className="text-sm">Please wait while we load your tasks...</p>
            </div>
          )}

          {/* Already planned tasks - read only */}
          {!tasksLoading && !isLoadingStandup && hasTodayStandup && standupTasks.length > 0 && (
            <div className="mb-4">
              <Card 
                className="border-primary/20 bg-primary/5 relative"
                onMouseEnter={() => setIsAlreadyPlannedHovered(true)}
                onMouseLeave={() => setIsAlreadyPlannedHovered(false)}
              >
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-primary" />
                    Already Planned Today
                    <span className="text-muted-foreground font-normal">({standupTasks.length})</span>
                  </CardTitle>
                  
                  {/* Send to Slack button on hover */}
                  {isAlreadyPlannedHovered && (
                    <button
                      onClick={handleSendPlannedToSlack}
                      disabled={isSendingPlanned || slackLoading}
                      className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-md bg-background border border-border hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {isSendingPlanned || slackLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Send className="h-3 w-3" />
                      )}
                      Send to Slack
                    </button>
                  )}
                </CardHeader>
                <CardContent className="py-2 px-4">
                  <TooltipProvider>
                    <div className="space-y-1">
                      {standupTasks.map(task => (
                        <div 
                          key={task.id} 
                          className="text-sm flex items-center gap-1.5 py-0.5 px-1 -mx-1 opacity-70"
                        >
                          <CheckCircle className="h-3 w-3 text-primary mr-1" />
                          <span className="font-medium">{task.brand_name}:</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {getTypeIcon(task.task_type)}
                            </TooltipTrigger>
                            <TooltipContent side="top">{getTypeLabel(task.task_type)}</TooltipContent>
                          </Tooltip>
                          <span className="truncate flex-1">{task.task_title}</span>
                          <span className="text-muted-foreground whitespace-nowrap">
                            (Spent: {formatTime(task.time_spent)})
                          </span>
                        </div>
                      ))}
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Available tasks to add */}
          {!tasksLoading && !isLoadingStandup && (
            <div className="space-y-4">
              <TooltipProvider>
                {Object.entries(tasksByDate).map(([dateKey, tasks]) => {
                  const displayDate = dateKey === 'No due date' 
                    ? 'No due date' 
                    : format(new Date(dateKey), 'MMM dd, yyyy');
                  const allDateSelected = tasks.every(t => selectedTaskIds.has(t.id));
                  
                  return (
                    <Card key={dateKey}>
                      <CardHeader className="py-3 px-4">
                        <CardTitle 
                          className="text-sm font-medium flex items-center gap-2 cursor-pointer"
                          onClick={() => handleSelectDateGroup(dateKey)}
                        >
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {displayDate}
                          <span className="text-muted-foreground font-normal">({tasks.length})</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="py-2 px-4">
                        <div className="space-y-1">
                          {tasks.map(task => (
                            <div 
                              key={task.id} 
                              className="text-sm flex items-center gap-1.5 py-0.5 cursor-pointer hover:bg-muted/50 rounded px-1 -mx-1"
                              onClick={() => handleToggle(task.id)}
                            >
                              <Checkbox
                                checked={selectedTaskIds.has(task.id)}
                                onCheckedChange={() => handleToggle(task.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="mr-1"
                              />
                              <span className="font-medium">{task.brand_name}:</span>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  {getTypeIcon(task.type)}
                                </TooltipTrigger>
                                <TooltipContent side="top">{getTypeLabel(task.type)}</TooltipContent>
                              </Tooltip>
                              <span className="truncate flex-1">{task.title}</span>
                              <span className="text-muted-foreground whitespace-nowrap">
                                (Est:{formatTime(task.time_estimated)} / Spent:{formatTime(task.time_spent)})
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </TooltipProvider>

              {filteredTasks.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  {searchQuery 
                    ? "No tasks match your search" 
                    : "No pending or in-progress tasks found"}
                </div>
              )}
            </div>
          )}
        </div>

        <Separator />

        <div className="flex flex-row justify-between gap-2 px-6 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyToClipboard}
            disabled={selectedTasks.length === 0}
          >
            <Copy className="h-4 w-4 mr-2" />
            Copy
          </Button>
          <Button
            onClick={handleAddToStandup}
            disabled={selectedTasks.length === 0 || isLoading || slackLoading}
          >
            <Sunrise className="h-4 w-4 mr-2" />
            Add {selectedTasks.length > 0 ? `${selectedTasks.length} tasks` : ''} to Today
          </Button>
        </div>
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col rounded-lg border bg-card text-card-foreground max-h-[70vh] overflow-hidden">
        <div className="px-6 pt-6 pb-0 flex-shrink-0">{headerContent}</div>
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">{bodyContent}</div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange ?? (() => {})}>
      <DialogContent className="max-w-2xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitleWithBug
            popupName="Day Start - Growth Standup"
            popupContext="/my-tasks-all-brands"
            isOpen={open}
          >
            {headerContent}
          </DialogTitleWithBug>
        </DialogHeader>
        {bodyContent}
      </DialogContent>
    </Dialog>
  );
};
